import { useMemo } from 'react';
import { interpolate } from '@/lib/pdfGen';

// HTML preview of the document - mirrors the PDF layout
export default function DocumentPreview({ template, values }) {
  if (template.type === 'invoice') return <InvoicePreview template={template} values={values} />;
  if (template.type === 'resume') return <ResumePreview values={values} />;
  return <DocPreview template={template} values={values} />;
}

function Page({ children, centered }) {
  return (
    <div className="paper grain shadow-[0_8px_40px_-12px_rgba(0,0,0,0.18)] mx-auto" style={{ width: '100%', aspectRatio: '595/842', padding: '64px', borderRadius: 6 }}>
      <div className={`h-full ${centered ? 'text-center flex flex-col justify-center items-center' : ''}`}>{children}</div>
    </div>
  );
}

function DocPreview({ template, values }) {
  return (
    <Page centered={template.centered}>
      {template.body.map((b, i) => {
        const t = interpolate(b.text, values);
        if (b.type === 'h1') return <h1 key={i} className="font-display font-bold text-2xl mb-4 tracking-tight">{t}</h1>;
        if (b.type === 'h2') return <h2 key={i} className="font-display font-semibold text-base mt-4 mb-1.5 tracking-tight">{t}</h2>;
        if (b.type === 'p') return <p key={i} className="text-[13px] leading-relaxed text-stone-700 mb-2">{t}</p>;
        if (b.type === 'spacer') return <div key={i} className="h-4" />;
        if (b.type === 'sign') {
          return (
            <div key={i} className={`mt-8 grid gap-6 ${b.labels.length === 1 ? 'grid-cols-1' : 'grid-cols-2'}`}>
              {b.labels.map((l, j) => (
                <div key={j}>
                  <div className="border-b border-stone-400 h-6" />
                  <div className="text-[11px] text-stone-500 mt-1">{interpolate(l, values)}</div>
                </div>
              ))}
            </div>
          );
        }
        return null;
      })}
    </Page>
  );
}

function InvoicePreview({ template, values }) {
  const items = values.items || [];
  const { subtotal, tax, total } = useMemo(() => {
    const subtotal = items.reduce((s, it) => s + (Number(it.quantity) || 0) * (Number(it.rate) || 0), 0);
    const tax = subtotal * ((Number(values.taxRate) || 0) / 100);
    return { subtotal, tax, total: subtotal + tax };
  }, [items, values.taxRate]);

  const label = template.receipt ? 'RECEIPT' : template.po ? 'PURCHASE ORDER' : template.quote ? 'QUOTE' : 'INVOICE';
  const billLabel = template.receipt ? 'RECEIVED FROM' : template.po ? 'VENDOR' : template.quote ? 'QUOTE FOR' : 'BILL TO';

  return (
    <Page>
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="font-display text-3xl font-bold text-orange-600 tracking-tight">{label}</h1>
          <div className="mt-4 text-sm">
            <div className="font-semibold text-stone-900">{values.businessName || '—'}</div>
            <div className="text-stone-500 whitespace-pre-line text-xs leading-relaxed mt-1">{values.businessAddress || ''}</div>
            <div className="text-stone-500 text-xs mt-1">{values.businessEmail || ''}</div>
          </div>
        </div>
        <div className="text-right text-xs">
          <Meta label={template.receipt ? 'Receipt #' : template.po ? 'PO #' : template.quote ? 'Quote #' : 'Invoice #'} value={values.invoiceNumber} />
          <Meta label="Date" value={values.invoiceDate} />
          {values.dueDate && <Meta label={template.po ? 'Delivery' : template.quote ? 'Valid Until' : 'Due Date'} value={values.dueDate} />}
        </div>
      </div>

      <div className="text-xs text-stone-500 uppercase tracking-wider mb-1">{billLabel}</div>
      <div className="text-sm font-semibold">{values.clientName || '—'}</div>
      <div className="text-xs text-stone-500 whitespace-pre-line">{values.clientAddress || ''}</div>

      <table className="w-full mt-6 text-sm">
        <thead>
          <tr className="bg-stone-100 text-stone-600">
            <th className="text-left p-2 font-medium">Description</th>
            <th className="text-right p-2 font-medium w-16">Qty</th>
            <th className="text-right p-2 font-medium w-24">Rate</th>
            <th className="text-right p-2 font-medium w-28">Amount</th>
          </tr>
        </thead>
        <tbody>
          {items.length === 0 && (
            <tr><td colSpan={4} className="text-center text-stone-400 py-6 text-xs">No line items yet.</td></tr>
          )}
          {items.map((it, i) => {
            const amt = (Number(it.quantity) || 0) * (Number(it.rate) || 0);
            return (
              <tr key={i} className="border-b border-stone-100">
                <td className="p-2 text-stone-800">{it.description}</td>
                <td className="p-2 text-right">{it.quantity}</td>
                <td className="p-2 text-right">${Number(it.rate || 0).toFixed(2)}</td>
                <td className="p-2 text-right">${amt.toFixed(2)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <div className="mt-4 flex justify-end">
        <div className="w-56 text-sm">
          <Row label="Subtotal" value={`$${subtotal.toFixed(2)}`} />
          {(Number(values.taxRate) || 0) > 0 && <Row label={`Tax (${values.taxRate}%)`} value={`$${tax.toFixed(2)}`} />}
          <div className="flex justify-between mt-2 pt-2 border-t border-stone-300">
            <span className="font-bold">Total</span>
            <span className="font-bold text-orange-600">${total.toFixed(2)}</span>
          </div>
        </div>
      </div>

      {values.notes && (
        <div className="mt-6 text-xs">
          <div className="font-semibold text-stone-700 mb-1">Notes</div>
          <div className="text-stone-600 whitespace-pre-line leading-relaxed">{values.notes}</div>
        </div>
      )}
    </Page>
  );
}

function Meta({ label, value }) {
  return (
    <div className="mb-2">
      <div className="text-stone-400 text-[10px] uppercase tracking-wider">{label}</div>
      <div className="text-stone-900 font-medium">{value || '—'}</div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between text-stone-600 py-0.5">
      <span>{label}</span>
      <span>{value}</span>
    </div>
  );
}

function ResumePreview({ values }) {
  const skills = (values.skills || '').split(',').map(s => s.trim()).filter(Boolean);
  return (
    <Page>
      <div>
        <h1 className="font-display text-3xl font-bold tracking-tight">{values.fullName || 'Your Name'}</h1>
        <div className="text-orange-600 text-sm font-medium mt-1">{values.title || 'Professional title'}</div>
        <div className="text-xs text-stone-500 mt-1">
          {[values.email, values.phone, values.location, values.website].filter(Boolean).join('  •  ')}
        </div>
        <div className="h-px bg-stone-200 my-4" />
        {values.summary && <Section title="Summary"><p className="text-[13px] leading-relaxed text-stone-700 whitespace-pre-line">{values.summary}</p></Section>}
        {values.experience && <Section title="Experience"><p className="text-[13px] leading-relaxed text-stone-700 whitespace-pre-line">{values.experience}</p></Section>}
        {values.education && <Section title="Education"><p className="text-[13px] leading-relaxed text-stone-700 whitespace-pre-line">{values.education}</p></Section>}
        {skills.length > 0 && (
          <Section title="Skills">
            <div className="flex flex-wrap gap-1.5">
              {skills.map((s, i) => (
                <span key={i} className="text-xs border border-stone-300 rounded-full px-2 py-0.5">{s}</span>
              ))}
            </div>
          </Section>
        )}
      </div>
    </Page>
  );
}

function Section({ title, children }) {
  return (
    <div className="mb-4">
      <div className="text-[11px] tracking-[0.18em] uppercase text-orange-600 font-bold mb-1.5">{title}</div>
      {children}
    </div>
  );
}
